import { useEffect, useState, type FormEvent } from "react";
import type { EventoAgenda, Usuario } from "../../../shared/types";
import { fechaHoyEnZonaSistema } from "../../../shared/timezone";
import { Boton } from "../../components/Boton";
import { Campo, Input, Select } from "../../components/Formulario";
import { ConfirmacionDialog } from "../../components/ConfirmacionDialog";
import { Insignia } from "../../components/Insignia";
import { Modal } from "../../components/Modal";
import { Tabla, type ColumnaTabla } from "../../components/Tabla";
import { useToast } from "../../components/Toast";
import { api, ErrorApi } from "../../lib/apiClient";

// Tres transiciones de estado que hoy expone la UI, todas confirmadas y
// todas reversibles vía la misma PATCH /agenda/:id/estado (el backend no
// impone una máquina de estados — ver auditoría de Agenda): un clic
// accidental en "Cumplir"/"Cancelar" ya no queda sin retroceso posible.
const CONFIRMACION_POR_ACCION: Record<
  "Cumplido" | "Cancelado" | "Pendiente",
  { titulo: string; mensaje: (titulo: string) => string; textoConfirmar: string; peligroso?: boolean }
> = {
  Cumplido: {
    titulo: "Cumplir vencimiento",
    mensaje: (t) => `¿Marcar "${t}" como cumplido?`,
    textoConfirmar: "Cumplir",
  },
  Cancelado: {
    titulo: "Cancelar vencimiento",
    mensaje: (t) => `¿Cancelar "${t}"? Vas a poder reabrirlo después si fue un error.`,
    textoConfirmar: "Cancelar vencimiento",
    peligroso: true,
  },
  Pendiente: {
    titulo: "Reabrir vencimiento",
    mensaje: (t) => `¿Volver a marcar "${t}" como pendiente?`,
    textoConfirmar: "Reabrir",
  },
};

export function PestanaAgenda({
  expedienteId,
  eventos,
  onCambio,
}: {
  expedienteId: string;
  eventos: EventoAgenda[];
  onCambio: () => void;
}) {
  const { mostrar } = useToast();
  const [modalAbierto, setModalAbierto] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [valores, setValores] = useState({
    tipo_evento: "",
    titulo: "",
    fecha_vencimiento: fechaHoyEnZonaSistema(),
    hora: "",
    responsable_id: "",
    prioridad: "Media",
  });

  const [enEdicion, setEnEdicion] = useState<EventoAgenda | null>(null);
  const [guardandoEdicion, setGuardandoEdicion] = useState(false);

  const [confirmando, setConfirmando] = useState<{
    evento: EventoAgenda;
    nuevoEstado: "Cumplido" | "Cancelado" | "Pendiente";
  } | null>(null);
  const [confirmandoCargando, setConfirmandoCargando] = useState(false);

  useEffect(() => {
    // Algunos roles no tienen usuarios.ver por defecto — si falla, se arma
    // un padrón de responsables asignables a partir de GET /agenda (mismo
    // criterio que AgendaPage.tsx), que sólo exige agenda.ver.
    api
      .get<{ items: Usuario[] }>("/usuarios")
      .then((d) => setUsuarios(d.items))
      .catch(() => {
        api
          .get<{ items: EventoAgenda[] }>("/agenda")
          .then((d) => {
            const vistos = new Map<string, string>();
            for (const e of d.items) {
              if (e.responsable_id && e.responsable_nombre) {
                vistos.set(e.responsable_id, e.responsable_nombre);
              }
            }
            setUsuarios(
              Array.from(vistos, ([id, nombre_completo]) => ({ id, nombre_completo }) as Usuario).sort((a, b) =>
                a.nombre_completo.localeCompare(b.nombre_completo),
              ),
            );
          })
          .catch(() => undefined);
      });
  }, []);

  function actualizar(campo: string, valor: string) {
    setValores((v) => ({ ...v, [campo]: valor }));
  }

  async function manejarEnvio(e: FormEvent) {
    e.preventDefault();
    setGuardando(true);
    try {
      await api.post("/agenda", { expediente_id: expedienteId, ...valores });
      mostrar("Vencimiento agendado.", "exito");
      setModalAbierto(false);
      onCambio();
    } catch (err) {
      mostrar(
        err instanceof ErrorApi ? err.message : "No se pudo agendar el evento.",
        "error",
      );
    } finally {
      setGuardando(false);
    }
  }

  function abrirEdicion(e: EventoAgenda) {
    setEnEdicion(e);
  }

  async function guardarEdicion(e: FormEvent) {
    e.preventDefault();
    if (!enEdicion) return;
    setGuardandoEdicion(true);
    try {
      await api.put(`/agenda/${enEdicion.id}`, {
        tipo_evento: enEdicion.tipo_evento,
        titulo: enEdicion.titulo,
        fecha_vencimiento: enEdicion.fecha_vencimiento,
        hora: enEdicion.hora || null,
        responsable_id: enEdicion.responsable_id || null,
        prioridad: enEdicion.prioridad,
      });
      mostrar("Vencimiento actualizado.", "exito");
      setEnEdicion(null);
      onCambio();
    } catch (err) {
      mostrar(err instanceof ErrorApi ? err.message : "No se pudo actualizar el evento.", "error");
    } finally {
      setGuardandoEdicion(false);
    }
  }

  async function confirmarCambioEstado() {
    if (!confirmando) return;
    setConfirmandoCargando(true);
    try {
      await api.patch(`/agenda/${confirmando.evento.id}/estado`, { estado: confirmando.nuevoEstado });
      mostrar(`Evento marcado como ${confirmando.nuevoEstado.toLowerCase()}.`, "exito");
      setConfirmando(null);
      onCambio();
    } catch (err) {
      mostrar(
        err instanceof ErrorApi ? err.message : "No se pudo actualizar el evento.",
        "error",
      );
    } finally {
      setConfirmandoCargando(false);
    }
  }

  const columnas: ColumnaTabla<EventoAgenda>[] = [
    { encabezado: "Vence", render: (e) => e.fecha_vencimiento },
    { encabezado: "Título", render: (e) => e.titulo },
    { encabezado: "Tipo", render: (e) => e.tipo_evento },
    {
      encabezado: "Responsable",
      render: (e) => e.responsable_nombre ?? "—",
    },
    {
      encabezado: "Prioridad",
      render: (e) => <Insignia texto={e.prioridad} tipo="prioridad" />,
    },
    { encabezado: "Estado", render: (e) => <Insignia texto={e.estado} /> },
    {
      encabezado: "Acciones",
      render: (e) => (
        <div className="flex flex-wrap gap-2">
          {(e.estado === "Pendiente" || e.estado === "Vencido") && (
            <>
              <button
                onClick={() => abrirEdicion(e)}
                className="text-sm font-medium text-secundario transition-colors hover:underline"
              >
                Editar
              </button>
              <Boton tamano="sm" variante="secundario" onClick={() => setConfirmando({ evento: e, nuevoEstado: "Cumplido" })}>
                Cumplir
              </Boton>
              <Boton tamano="sm" variante="fantasma" onClick={() => setConfirmando({ evento: e, nuevoEstado: "Cancelado" })}>
                Cancelar
              </Boton>
            </>
          )}
          {(e.estado === "Cumplido" || e.estado === "Cancelado") && (
            <Boton tamano="sm" variante="secundario" onClick={() => setConfirmando({ evento: e, nuevoEstado: "Pendiente" })}>
              Reabrir
            </Boton>
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="mb-3 flex justify-end">
        <Boton tamano="sm" onClick={() => setModalAbierto(true)}>
          + Nuevo Vencimiento
        </Boton>
      </div>
      <Tabla
        columnas={columnas}
        filas={eventos}
        claveFila={(e) => e.id}
        tituloVacio="Sin eventos en agenda"
        descripcionVacio="Agendá un vencimiento procesal o recordatorio."
      />
      <Modal
        titulo="Nuevo vencimiento"
        abierto={modalAbierto}
        onCerrar={() => setModalAbierto(false)}
      >
        <form onSubmit={manejarEnvio} className="space-y-4">
          <Campo etiqueta="Tipo de evento" obligatorio>
            <Input
              required
              value={valores.tipo_evento}
              onChange={(e) => actualizar("tipo_evento", e.target.value)}
              placeholder="Vencimiento Procesal / Audiencia / Recordatorio"
            />
          </Campo>
          <Campo etiqueta="Título" obligatorio>
            <Input
              required
              value={valores.titulo}
              onChange={(e) => actualizar("titulo", e.target.value)}
            />
          </Campo>
          <div className="grid grid-cols-2 gap-4">
            <Campo etiqueta="Fecha de vencimiento" obligatorio>
              <Input
                type="date"
                required
                value={valores.fecha_vencimiento}
                onChange={(e) =>
                  actualizar("fecha_vencimiento", e.target.value)
                }
              />
            </Campo>
            <Campo etiqueta="Hora">
              <Input type="time" value={valores.hora} onChange={(e) => actualizar("hora", e.target.value)} />
            </Campo>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Campo etiqueta="Responsable">
              {usuarios.length > 0 ? (
                <Select value={valores.responsable_id} onChange={(e) => actualizar("responsable_id", e.target.value)}>
                  <option value="">Sin asignar</option>
                  {usuarios.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.nombre_completo}
                    </option>
                  ))}
                </Select>
              ) : (
                <p className="flex h-[38px] items-center text-sm text-texto-secundario">Sin asignar</p>
              )}
            </Campo>
            <Campo etiqueta="Prioridad">
              <Select
                value={valores.prioridad}
                onChange={(e) => actualizar("prioridad", e.target.value)}
              >
                <option value="Alta">Alta</option>
                <option value="Media">Media</option>
                <option value="Baja">Baja</option>
              </Select>
            </Campo>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Boton
              type="button"
              variante="secundario"
              onClick={() => setModalAbierto(false)}
              disabled={guardando}
            >
              Cancelar
            </Boton>
            <Boton type="submit" cargando={guardando}>
              Guardar
            </Boton>
          </div>
        </form>
      </Modal>

      <Modal titulo="Editar vencimiento" abierto={Boolean(enEdicion)} onCerrar={() => setEnEdicion(null)}>
        {enEdicion && (
          <form onSubmit={guardarEdicion} className="space-y-4">
            <Campo etiqueta="Tipo de evento" obligatorio>
              <Input
                required
                value={enEdicion.tipo_evento}
                onChange={(e) => setEnEdicion((v) => v && { ...v, tipo_evento: e.target.value })}
              />
            </Campo>
            <Campo etiqueta="Título" obligatorio>
              <Input
                required
                value={enEdicion.titulo}
                onChange={(e) => setEnEdicion((v) => v && { ...v, titulo: e.target.value })}
              />
            </Campo>
            <div className="grid grid-cols-2 gap-4">
              <Campo etiqueta="Fecha de vencimiento" obligatorio>
                <Input
                  type="date"
                  required
                  value={enEdicion.fecha_vencimiento}
                  onChange={(e) => setEnEdicion((v) => v && { ...v, fecha_vencimiento: e.target.value })}
                />
              </Campo>
              <Campo etiqueta="Hora">
                <Input
                  type="time"
                  value={enEdicion.hora ?? ""}
                  onChange={(e) => setEnEdicion((v) => v && { ...v, hora: e.target.value })}
                />
              </Campo>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Campo etiqueta="Responsable">
                {(() => {
                  // Si GET /usuarios no está disponible para este rol (ver
                  // efecto de carga arriba), al menos se conserva la opción
                  // del responsable ya asignado — evita mostrar "Sin
                  // asignar" para un evento que sí tiene responsable.
                  const opciones =
                    usuarios.length > 0
                      ? usuarios
                      : enEdicion.responsable_id && enEdicion.responsable_nombre
                        ? [{ id: enEdicion.responsable_id, nombre_completo: enEdicion.responsable_nombre }]
                        : [];
                  return opciones.length > 0 ? (
                    <Select
                      value={enEdicion.responsable_id ?? ""}
                      onChange={(e) => setEnEdicion((v) => v && { ...v, responsable_id: e.target.value })}
                    >
                      <option value="">Sin asignar</option>
                      {opciones.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.nombre_completo}
                        </option>
                      ))}
                    </Select>
                  ) : (
                    <p className="flex h-[38px] items-center text-sm text-texto-secundario">Sin asignar</p>
                  );
                })()}
              </Campo>
              <Campo etiqueta="Prioridad">
                <Select
                  value={enEdicion.prioridad}
                  onChange={(e) => setEnEdicion((v) => v && { ...v, prioridad: e.target.value as EventoAgenda["prioridad"] })}
                >
                  <option value="Alta">Alta</option>
                  <option value="Media">Media</option>
                  <option value="Baja">Baja</option>
                </Select>
              </Campo>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Boton type="button" variante="secundario" onClick={() => setEnEdicion(null)} disabled={guardandoEdicion}>
                Cancelar
              </Boton>
              <Boton type="submit" cargando={guardandoEdicion}>
                Guardar cambios
              </Boton>
            </div>
          </form>
        )}
      </Modal>

      {confirmando && (
        <ConfirmacionDialog
          abierto={Boolean(confirmando)}
          titulo={CONFIRMACION_POR_ACCION[confirmando.nuevoEstado].titulo}
          mensaje={CONFIRMACION_POR_ACCION[confirmando.nuevoEstado].mensaje(confirmando.evento.titulo)}
          textoConfirmar={CONFIRMACION_POR_ACCION[confirmando.nuevoEstado].textoConfirmar}
          peligroso={CONFIRMACION_POR_ACCION[confirmando.nuevoEstado].peligroso}
          cargando={confirmandoCargando}
          onConfirmar={confirmarCambioEstado}
          onCancelar={() => setConfirmando(null)}
        />
      )}
    </div>
  );
}
